/**
 * see {@tutorial corner-tutorial}
 * @category Corners
 * @module Corner
 *
 *
 */
import * as CornerTypes from "../types/CornerTypes";
import * as VuexTypes from "../types/VuexTypes";
import * as CommonTypes from "../types/CommonTypes";

import { rangeLabelToIndex } from "../../components/process-design/cell/Utils";
import { hfInstance } from "../Cells";
import { CORNER_TYPES } from "./constants";
import { ExtendedRBush } from "../helpers/ExtendedRBush";
import { ExportedCellChange } from "hyperformula";
import * as HyperFormula from "hyperformula";
import { arrayToObjectRange } from "../RangeStyle";
/**
 * `pointerTree` is an instance of `ExtendedRBush`.
 *
 * It stores all pointer ranges of corners to enable fast lookup and spatial querying.
 * When a cell changes, the tree is used to determine if it intersects with any pointer range.
 *
 * Each entry in the tree contains:
 * - `keyPointer`: Name of the key pointer.
 * - `dimension`: Either `"row"` or `"column"`.
 * - `cornerArea`: Identifier for the corner this pointer belongs to.
 * - `index`: Index of the key pointer (since multiple pointers can share the same name).
 *
 */
export let pointerTree = new ExtendedRBush();
/**
 * Same as pointer tree. but it store keyPointer data
 */
export let keyPointerTree = new ExtendedRBush();

const cornerTables = {
  styleareas: "styleCorner/loadStyleAreas",
  stylesettings: "styleCorner/loadStyleSettings",
  formulaareas: "formulaCorner/loadFormulaAreas",
};
/**
 * Extracts the corner type from a corner area name.
 *
 * Each corner name follows the format: {CornerType}_XY
 * By removing the trailing "_XY", we get the corner type.
 *
 * @param {string} area - The full corner area name (e.g., "Style_01").
 * @returns {string} The extracted corner type (e.g., "Style").
 */
function getCornerTypeByAreaName(area) {
  return area.replace(/_\d+$/, "");
}

/**
 * Retrieves all changed corners along with relevant data, including:
 * - Which key pointer was changed
 * - Lists of changed columns and rows within that key pointer
 * @param {ExportedCellChange[]} changedCells - List of changed cells to evaluate.
 * @returns {CornerTypes.CornerKeyPointerChangeMap}
 */
function getChangedCorners(changedCells) {
  /** @type {CornerTypes.CornerKeyPointerChangeMap} */
  const map = {};
  changedCells.forEach((item) => {
    const address = item.address;

    // add only current sheet
    if (!address) return;
    const results = pointerTree.search({
      minX: address.col,
      minY: address.row,
      maxX: address.col,
      maxY: address.row,
    });
    results.forEach((result) => {
      const { cornerArea, dimension, keyPointer, index } = result.data;

      if (!map[cornerArea]) map[cornerArea] = {};
      if (!map[cornerArea][keyPointer]) map[cornerArea][keyPointer] = {};

      map[cornerArea][keyPointer][index] ??= { row: [], column: [] };
      // For column key pointers, the affected data lies in the rows.
      // For row key pointers, the affected data lies in the columns.
      // Example: A column key pointer like "sumlevel" will have updated rows.
      map[cornerArea][keyPointer][index][dimension === "column" ? "row" : "column"].push(
        dimension === "column" ? address.row : address.col
      );
    });
  });
  return map;
}

/**
 * Checks if the key pointer data of any corners has changed.
 *
 * @param {Object[]} changedCells - List of changed cells to evaluate.
 */
function isCornerStructureChanged(changedCells) {
  let changed = false;
  changedCells.forEach((item) => {
    const address = item.address;

    // add only current sheet
    if (!address) return;
    const result = keyPointerTree.search({
      minX: address.col,
      minY: address.row,
      maxX: address.col,
      maxY: address.row,
    });
    if (result.length > 0) return (changed = true);
  });
  return changed;
}

export default {
  namespaced: true,
  state: () => ({
    corners: {},
  }),
  mutations: {},
  actions: {
    /**
     * @function
     * @name module:Corner.loadTables
     * @description Reloads all corner-related tables when a sheet is opened.
     *
     * Each corner (e.g., "Style" corner) has its own set of associated tables.
     * For example, the "Style" corner includes: ['styleareas', 'stylesettings'].
     *
     * TODO: Optimize to trigger only when a relevant table changes.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     */
    async loadTables({ commit, dispatch, state, rootState }) {
      const tables = rootState.table.tables;
      tables.forEach(async (table) => {
        const func = cornerTables[table.tableName.toLowerCase()];
        if (func) {
          await dispatch(func, null, { root: true });
        }
      });
    },
    /**
     * @function
     * @name module:Corner.loadCorners
     * @description Retrieves all valid corner names and loads the following data for each corner:
     *
     * - area (name and id of corner) <br>
     * - sheetHfId <br>
     * - cornerType <br>
     * - scope <br>
     * - columnRange <br>
     * - rowRange <br>
     *
     * @param {VuexTypes.VuexContext} context - Vuex action context (destructured).
     */
    async loadCorners({ commit, dispatch, state, rootState, getters, rootGetters }) {
      state.corners = {};
      const cornerAreas = getters.cornerNamesFromNameRanges;
      // TODO: remove corner names which only have row or column but not both

      cornerAreas.forEach((cornerArea) => {
        const columnData = rootState.table.groupedNamedExpressions[`${cornerArea}column`];
        const rowData = rootState.table.groupedNamedExpressions[`${cornerArea}row`];

        const sheetHfId = rootGetters["sheets/getHfIndex"](columnData.sheet_id);

        const columnExpression = columnData.expressions[0].expression?.replaceAll("$", "");
        const columnRange = rangeLabelToIndex(columnExpression);

        const rowExpression = rowData.expressions[0].expression?.replaceAll("$", "");
        const rowRange = rangeLabelToIndex(rowExpression);

        state.corners[cornerArea] = {
          area: cornerArea,
          sheetHfId,
          cornerType: getCornerTypeByAreaName(cornerArea),
          scope: columnData.scope,
          columnRange,
          rowRange,
          columnKeyPointers: {},
          rowKeyPointers: {},
        };
      });

      await dispatch("loadPointerData");
      await dispatch("loadKeyPointerData");
      dispatch("loadSatelliteTableNames");
    },
    /**
     * @function
     * @name module:Corner.loadPointerData
     * @description Loads all pointer data for all corners and adds it pointer tree
     *
     * also following data is loaded for each corner
     * - columnKeyPointers
     * - rowKeyPointers
     *
     * A "pointer" represents the value range of a key pointer,
     * and its range is one cell below the key pointer range.
     *
     * For example:
     * If the key pointer range is [10, 12, 10, 200],
     * then the pointer range will be [11, 12, 11, 200].
     *
     * @param {VuexTypes.VuexContext} context - Vuex action context (destructured).
     */
    loadPointerData({ commit, dispatch, state, rootState, getters, rootGetters }) {
      const namedExpressions = rootState.table.groupedNamedExpressions;
      const cornerNames = Object.keys(state.corners);

      pointerTree.clear();
      cornerNames.forEach((cornerName) => {
        const columnNamedRange = namedExpressions[`${cornerName}column`];
        const rowNamedRange = namedExpressions[`${cornerName}row`];
        const columnRangeLabel = columnNamedRange.expressions[0].expression;
        const columnRange = rangeLabelToIndex(columnRangeLabel);
        const sheet = rootState.sheets.allSheets.find((item) => item.id === columnNamedRange.sheet_id);
        const sheetHfId = sheet.sheetId;

        const rowRangeLabel = rowNamedRange.expressions[0].expression;
        const rowRange = rangeLabelToIndex(rowRangeLabel);
        if (!columnRange || !rowRange) return;

        // Get KeyPointers Data
        const columnKeyPointerArray = hfInstance.getRangeValues({
          start: { sheet: sheetHfId, col: columnRange[0], row: columnRange[1] },
          end: { sheet: sheetHfId, col: columnRange[2], row: columnRange[1] },
        });

        columnKeyPointerArray[0].forEach((item, index) => {
          if (!item) return;
          item = String(item).toLowerCase().trim();
          const range = [columnRange[0] + index, rowRange[1] + 1, columnRange[0] + index, rowRange[3]];
          const data = {
            keyPointer: item,
            dimension: "column",
            cornerArea: cornerName,
          };

          const columnKeyPointers = state.corners[cornerName].columnKeyPointers;
          if (!columnKeyPointers[item]) columnKeyPointers[item] = [];
          data.index = columnKeyPointers[item].length;
          state.corners[cornerName].columnKeyPointers[item].push({ range, ...data });
          pointerTree.insert({
            minX: range[0],
            minY: range[1],
            maxX: range[2],
            maxY: range[3],
            data,
          });
        });

        const rowKeyPointerArray = hfInstance.getRangeValues({
          start: { sheet: sheetHfId, col: rowRange[0], row: rowRange[1] },
          end: { sheet: sheetHfId, col: rowRange[0], row: rowRange[3] },
        });
        rowKeyPointerArray.forEach((item, index) => {
          item = item[0];
          if (!item) return;
          item = String(item).toLowerCase().trim();

          const range = [columnRange[0] + 1, rowRange[1] + index, columnRange[2], rowRange[1] + index];
          const data = {
            keyPointer: item,
            dimension: "row",
            cornerArea: cornerName,
          };

          const rowKeyPointers = state.corners[cornerName].rowKeyPointers;
          if (!rowKeyPointers[item]) {
            rowKeyPointers[item] = [];
          }
          data.index = rowKeyPointers[item].length;
          state.corners[cornerName].rowKeyPointers[item].push({ range, ...data });

          pointerTree.insert({
            minX: range[0],
            minY: range[1],
            maxX: range[2],
            maxY: range[3],
            data,
          });
        });
      });
    },
    /**
     * @function
     * @name module:Corner.loadKeyPointerData
     * @description Loads all key pointer data for all corners and adds it to the keyPointerTree.
     *
     * Corner functionality is driven by key pointers, which come in two types:
     * - Column key pointers (e.g., "sumlevel", "row")
     * - Row key pointers (e.g., "sum", "target")
     *
     * @param {VuexTypes.VuexContext} context - Vuex action context (destructured).
     */
    loadKeyPointerData({ commit, dispatch, state, rootState }) {
      /**@type {CornerTypes.Corners} */
      const corners = state.corners;
      keyPointerTree.clear();
      Object.entries(corners).forEach(([cornerName, corner]) => {
        const columnRange = corner.columnRange;
        const rowRange = corner.rowRange;

        const columnKeyPointerRange = [columnRange[0], columnRange[1], columnRange[2], columnRange[1]];
        const rowKeyPointerRange = [rowRange[0], rowRange[1], rowRange[0], rowRange[3]];

        keyPointerTree.insert({
          minX: columnKeyPointerRange[0],
          minY: columnKeyPointerRange[1],
          maxX: columnKeyPointerRange[2],
          maxY: columnKeyPointerRange[3],
          data: {
            cornerArea: corner.area,
            cornerType: corner.cornerType,
            dimension: "column",
          },
        });
        keyPointerTree.insert({
          minX: rowKeyPointerRange[0],
          minY: rowKeyPointerRange[1],
          maxX: rowKeyPointerRange[2],
          maxY: rowKeyPointerRange[3],
          data: {
            cornerArea: corner.area,
            cornerType: corner.cornerType,
            dimension: "row",
          },
        });
      });
    },
    loadSatelliteTableNames({ commit, dispatch, state, rootState, rootGetters }) {
      const satTableNames = new Set();
      Object.values(state.corners).forEach((/** @type {CornerTypes.Corner} */ corner) => {
        if (corner.cornerType !== CORNER_TYPES.SATELLITE) return;
        const allRange = [corner.columnRange[0], corner.rowRange[1], corner.columnRange[2], corner.rowRange[3]];
        const result = rootState.table.tableTree.search(arrayToObjectRange(allRange));
        result.forEach((item) => {
          if (item.data?.table?.tableName) {
            const tableSheetHfId = rootGetters["sheets/getHfIndex"](item.data.table.sheetId);
            if (tableSheetHfId === corner.sheetHfId) satTableNames.add(item.data.table.tableName);
          }
        });
      });
      state.satTableNames = satTableNames;
    },
    /**
     * @function
     * @name module:Corner.setChangedCells
     * @description Given a list of changed cells, this function:
     * - Identifies which corners have been affected
     * - Triggers updates for those corners with the relevant changed pointers
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context (destructured).
     * @param {ExportedCellChange[]} changedCells - The list of changed cells to evaluate.
     */
    async setChangedCells({ commit, dispatch, state, rootState, rootGetters }, changedCells) {
      if (isCornerStructureChanged(changedCells)) return await dispatch("loadCorners");
      const rowsChanged = new Set();
      let tableChanged;
      changedCells.forEach((changedCell) => {
        if (!changedCell?.name) {
          const results = rootState.table.tableTree.search({
            minX: changedCell.address.col,
            minY: changedCell.address.row,
            maxX: changedCell.address.col,
            maxY: changedCell.address.row,
          });
          results.forEach((item) => {
            const tableName = item.data?.table?.tableName;
            if (state.satTableNames.has(tableName)) {
              tableChanged = tableName;
              const range = rangeLabelToIndex(item.data.table.tableRange);
              if (!range) return;
              const change = changedCell.address.row - range[1] - 2;
              if (change >= 0) rowsChanged.add(change);
            }
          });
        }
      });
      if (rowsChanged.size > 0) {
        dispatch("satelliteCorner/save", { rowsChanged, tableName: tableChanged }, { root: true });
      }
      const pointerMap = getChangedCorners(changedCells);
      Object.entries(pointerMap).forEach(([area, data]) => {
        const cornerType = getCornerTypeByAreaName(area)?.toLowerCase().trim();
        if (cornerType === CORNER_TYPES.STYLE)
          dispatch("styleCorner/applyStyles", { styleArea: area, data }, { root: true });
        else if (cornerType === CORNER_TYPES.FORMULA) {
          dispatch("formulaCorner/applyFormulas", { area, data }, { root: true });
        }
      });
    },
    async resetCorners({ commit, state, rootState, dispatch }) {
      await dispatch("styleCorner/resetState", null, { root: true });
      await dispatch("formulaCorner/resetState", null, { root: true });
    },
  },
  getters: {
    /**
     * @callback GetNamedExpressionAsArrayCallback
     * @param {string} namedExpression - The named expression for which the data matrix is needed.
     * @returns {Array<Array<any>>} A 2D array (matrix) representing the data in the named range.
     */

    /**
     * @function
     * @name module:Corner.getNamedExpressionAsArray
     * @description Returns the matrix of data for a given named range.
     *
     * @returns {GetNamedExpressionAsArrayCallback} A function that takes a named expression and returns a 2D array (matrix).
     */
    getNamedExpressionAsArray: (state, getters, rootState) => (namedExpression) => {
      /** @type {HyperFormula.NamedExpression} */
      const expr = hfInstance.getNamedExpression(namedExpression);
      if (!expr?.expression) return null;

      const match = expr.expression.match(/([A-Za-z0-9_]+)!(.+)/);
      if (!match) {
        throw new Error(`Invalid named expression format: ${expr}`);
      }

      let [, sheetName, rangeLabel] = match;
      rangeLabel = rangeLabel.replaceAll("$", "");
      const range = rangeLabelToIndex(rangeLabel);
      const sheet = rootState.sheets.allSheets.find((item) => item.name === sheetName);
      if (!sheet || !range) throw new Error(`No such Sheet`);

      return hfInstance.getRangeValues({
        start: { sheet: sheet.sheetId, col: range[0], row: range[1] },
        end: { sheet: sheet.sheetId, col: range[2], row: range[3] },
      });
    },
    /**
     * @callback IsTableValidCallback
     * @param {string} tableName - The name of the table to check.
     * @param {string[]} requiredHeaders - The list of headers that must be present in the table.
     * @returns {boolean} Returns `true` if the table exists and contains all required headers, otherwise `false`.
     */

    /**
     * @function
     * @name module:Corner.isTableValid
     * @description Checks whether a table exists and contains all the required headers.
     *
     * @returns {IsTableValidCallback} A function that takes a table name and a list of required headers,
     *                                 and returns `true` if the table is valid, otherwise `false`.
     */
    isTableValid: (state, getters, rootState) => (tableName, requiredHeaders) => {
      let headers = getters.getNamedExpressionAsArray(`${tableName}..header`);
      if (!headers) return false;

      headers = headers[0];
      const headerSet = new Set(headers.map((item) => String(item).toLowerCase().trim()));

      return requiredHeaders.every((header) => headerSet.has(header.toLowerCase().trim()));
    },
    /**
     * @function
     * @name module:Corner.cornerNamesFromNameRanges
     * @description Retrieve valid corner names for all supported corner types.
     *
     * A valid corner must follow the format: {CornerType}_XY,
     * and must have both associated named ranges:
     * - {CornerType}_XYColumn
     * - {CornerType}_XYRow
     *
     */
    cornerNamesFromNameRanges: (state, getters, rootState, rootGetters) => {
      const namedExpressions = rootState.table.groupedNamedExpressions;

      const config = [
        {
          type: "Style",
          areaNames: rootGetters["styleCorner/activeStyleAreaNames"](),
        },
        {
          type: "Formula",
          areaNames: rootGetters["formulaCorner/activeFormulaAreas"](),
        },
        {
          type: "SAT",
          areaNames: null,
        },
      ];

      const cornerNames = new Set();

      for (const { type, areaNames } of config) {
        const nameMap = new Map(); // name => { hasRow: bool, hasColumn: bool }

        const columnRegex = new RegExp(`^${type}_[0-9]{2}Column$`, "i");
        const rowRegex = new RegExp(`^${type}_[0-9]{2}Row$`, "i");

        for (const key of Object.keys(namedExpressions)) {
          let name = null;

          if (columnRegex.test(key)) {
            name = key
              .toLowerCase()
              .replace(/column$/i, "")
              .trim();
            const entry = nameMap.get(name) || { hasRow: false, hasColumn: false };
            entry.hasColumn = true;
            nameMap.set(name, entry);
          }

          if (rowRegex.test(key)) {
            name = key.toLowerCase().replace(/row$/i, "").trim();
            const entry = nameMap.get(name) || { hasRow: false, hasColumn: false };
            entry.hasRow = true;
            nameMap.set(name, entry);
          }
        }

        for (const [name, flags] of nameMap.entries()) {
          if (flags.hasRow && flags.hasColumn && (areaNames === null || areaNames.has(name))) {
            cornerNames.add(name);
          }
        }
      }
      return [...cornerNames];
    },
  },
};
